/**
 * Settings > Install Week (INSTALL-WEEK-03, Seth 2026-08-20: "by default it
 * should only show installers. And I should be able to edit this somewhere
 * in settings who shows up by default.")
 *
 * The list here IS the grid's default lanes (pegasus_install_week_crew).
 * One-day guests ("Tony's plumbing job Thursday") are added on the grid
 * itself — the + on each day's header — not here.
 */
import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useAllActiveTechs } from "@/features/board/api";
import { useInstallCrew, useAddInstallCrew, useRemoveInstallCrew } from "@/features/team/team";

export function InstallWeekSettings() {
  const canManage = useCan("team.install_week");
  const { data: crew, isLoading } = useInstallCrew();
  const { data: techs } = useAllActiveTechs();
  const add = useAddInstallCrew();
  const remove = useRemoveInstallCrew();
  const [pick, setPick] = useState("");

  const inCrew = new Set((crew ?? []).map((c) => c.empno));
  const candidates = (techs ?? []).filter((t) => !inCrew.has(t.empno));

  return (
    <div className="space-y-4">
      <Card className="p-5">
        {isLoading ? (
          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {(crew ?? []).map((c) => (
              <span key={c.empno}
                className="inline-flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/60 px-2.5 py-1.5 text-sm">
                <span className="font-semibold text-slate-800">{c.name}</span>
                <span className="text-xs text-slate-400">#{c.empno}</span>
                {canManage && (
                  <button type="button" title="Remove from the default lanes" disabled={remove.isPending}
                    onClick={() => remove.mutate(c.empno)}
                    className="rounded p-0.5 text-slate-400 transition-colors hover:bg-white hover:text-rose-500">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
            {(crew ?? []).length === 0 && (
              <p className="text-sm text-slate-400">
                No default crew picked yet — the grid is guessing from recent installs. Add your installers below.
              </p>
            )}
          </div>
        )}

        {canManage && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <select value={pick} onChange={(e) => setPick(e.target.value)}
              className="rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none">
              <option value="">Pick a tech…</option>
              {candidates.map((t) => (
                <option key={t.empno} value={t.empno}>
                  {[(t.firstname ?? "") || t.empname || t.empno, t.lastname ?? ""].join(" ").trim()}
                </option>
              ))}
            </select>
            <button type="button" disabled={!pick || add.isPending}
              onClick={() => add.mutate(pick, { onSuccess: () => setPick("") })}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 disabled:opacity-50">
              {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to Default Lanes
            </button>
            <InfoTip label="About one-day adds">
              These techs get a lane every week. For a one-day add, like a plumbing job you want to see Thursday, use the + on
              that day&apos;s header on the grid.
            </InfoTip>
            {(add.isError || remove.isError) && (
              <p className="text-xs font-medium text-rose-600">Couldn&apos;t save that change — try again.</p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
